import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import {
  BankLinkApi,
  BankVerificationApi,
  PersonalLoanApplicationBankAccountApi,
  PersonalLoanApplicationBankAccountVerificationApi
} from '../../../../../generated/internalApi/api/api';

import {
  LDCommonWebApiResponseApiResponseSystemCollectionsGenericListLDCLPBPPublicApiModelsBanksInstitution
    as InstitutionsResponse,
  LDCLPBPPublicApiModelsBanksBankLinkBeginRequest,
  LDCLPBPPublicApiModelsBanksBankLinkBeginResponse,
  LDCLPBPPublicApiModelsBanksBankLinkConfirmRequest,
  LDCLPBPPublicApiModelsBanksBankLinkConfirmResponse,
  LDCLPBPPublicApiModelsBanksInstitution,
  LDCLPBPPublicApiModelsBanksInitiateBankLinkingRequest,
  LDCLPBPPublicApiModelsBanksInitiateBankLinkingResponse,
  LDCLPBPPublicApiModelsBanksInitiateTrialDepositsRequest,
  LDCLPBPPublicApiModelsBanksInitiateTrialDepositsResponse,
  LDCLPBPPublicApiModelsBanksMFA,
  LDCLPBPPublicApiModelsBanksBankSearchRequest,
  LDCLPBPPublicApiModelsBanksBankSearchResponse,
  LDCLPBPPublicApiModelsBanksManualLinkBankAccountRequest,
  LDCLPBPPublicApiModelsBanksManualLinkBankAccountResponse,
  LDCLPBPPublicApiModelsBanksSubmitBankLinkingMFARequest,
  LDCLPBPPublicApiModelsBanksSubmitBankLinkingMFAResponse,
  LDCLPBPPublicApiModelsBanksSetBankAccountRequest
} from '../../../../../generated/internalApi/model/models';

import {
  BankAccountVerificationData,
  BankAccountVerificationResponse,
  TrialDepositsRequest,
  TrialDepositsResponse,
  TrialDepositsResponseData
} from '../../models/index';

import { SessionService } from '../session/shared/session/session-service/session.service';
import { ApiService } from '../api.service';

@Injectable()
export class BankLinkingService extends ApiService {
  private authorization: string;
  private loanApplicationGuid: string;

  constructor(
    private bankLinkApi: BankLinkApi,
    private bankVerificationApi: BankVerificationApi,
    private bankAccountVerificationApi: PersonalLoanApplicationBankAccountVerificationApi,
    private personalLoanApplicationBankAccountApi: PersonalLoanApplicationBankAccountApi,
    protected sessionService: SessionService) {

    super(sessionService);
    this.authorization = sessionService.data.authData.token;
    this.loanApplicationGuid = this.sessionService.personalLoanSession.data.loanApplicationGuid;
  }

  getBankAccountVerification(): Observable<BankAccountVerificationData> {
    return this.bankAccountVerificationApi.personalLoanApplicationBankAccountVerificationGetBankAccountVerificationByLoanApplicationGuid(
      this.loanApplicationGuid,
      this.authorization
    )
    .map((response: BankAccountVerificationResponse) => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  getBankNameByRoutingNumber(routingNumber: string): Observable<LDCLPBPPublicApiModelsBanksBankSearchResponse> {
    let query = <LDCLPBPPublicApiModelsBanksBankSearchRequest>{
      routingNumber
    };

    return this.bankVerificationApi.bankVerificationGetInstitutions_1(
      query,
      this.authorization
    )
    .map(response => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  getDefaultInstitutions(search: string = 'default'): Observable<any> {
    return this.bankVerificationApi.bankVerificationGetInstitutions(
      search,
      this.authorization
    )
    .map(response => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  postBankLinkBegin(): Observable<LDCLPBPPublicApiModelsBanksBankLinkBeginResponse> {
    let data: LDCLPBPPublicApiModelsBanksBankLinkBeginRequest = {
      borrowerGuid: this.sessionService.personalLoanSession.data.borrowerData.borrowerGuid,
      loanApplicationGuid: this.sessionService.personalLoanSession.data.loanApplicationGuid
    };
    return this.bankLinkApi.bankLinkBankLinkBegin(data, this.authorization)
      .map(response => response.data);
  }

  postBankLinkConfirm(queryParams: any): Observable<LDCLPBPPublicApiModelsBanksBankLinkConfirmResponse> {
    let data: LDCLPBPPublicApiModelsBanksBankLinkConfirmRequest = {
      success: !!queryParams.success,
      reasonCode: queryParams.reasonCode,
      error: queryParams.error,
      loanApplicationGuid: this.sessionService.personalLoanSession.data.loanApplicationGuid,
      borrowerGuid: this.sessionService.personalLoanSession.data.borrowerData.borrowerGuid,
      bankingCustomerGuid: queryParams.bankingCustomerGuid
    };
    return this.bankLinkApi.bankLinkBankLinkConfirm(data, this.authorization)
      .map(response => response.data);
  }

  postInstantInitiation(
    bank: LDCLPBPPublicApiModelsBanksInstitution,
    username: string,
    password: string): Observable<LDCLPBPPublicApiModelsBanksInitiateBankLinkingResponse> {

    let data: LDCLPBPPublicApiModelsBanksInitiateBankLinkingRequest = {
      bank: bank,
      loanApplicationGuid: this.loanApplicationGuid,
      username: username,
      password: password,
      acceptedTermsAndConditions: true
    };

    return this.bankVerificationApi.bankVerificationInitiateBankLinking(
      data,
      this.authorization
    )
    .map(response => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  postMfaAnswer(
    bankVerifyGuid: string,
    mfaAnswer: LDCLPBPPublicApiModelsBanksMFA): Observable<LDCLPBPPublicApiModelsBanksSubmitBankLinkingMFAResponse> {

    let data: LDCLPBPPublicApiModelsBanksSubmitBankLinkingMFARequest = {
      mfa: mfaAnswer
    };

    return this.bankVerificationApi.bankVerificationSubmitBankLinkingMFA(
      bankVerifyGuid,
      data,
      this.authorization
    )
    .map(response => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  postAccountSelection(bankAccountGuid: string): Observable<void> {
    let data: LDCLPBPPublicApiModelsBanksSetBankAccountRequest = {
      bankAccountGuid: bankAccountGuid
    };

    return this.personalLoanApplicationBankAccountApi.personalLoanApplicationBankAccountSetBankAccountForLoanApplication(
      this.loanApplicationGuid,
      data,
      this.authorization
    )

    // Empty 206 is returned.
    .map(response => {})
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  postManualNoTrialDeposits(
    bankName: string,
    routingNumber: string,
    accountNumber: string,
    accountType: LDCLPBPPublicApiModelsBanksManualLinkBankAccountRequest.AccountTypeEnum,
    firstName: string,
    lastName: string,
    acceptedTermsAndConditions: boolean): Observable<LDCLPBPPublicApiModelsBanksManualLinkBankAccountResponse> {

    let data: LDCLPBPPublicApiModelsBanksManualLinkBankAccountRequest = {
      accountNumber,
      accountType,
      bankName,
      firstName,
      lastName,
      loanApplicationGuid: this.loanApplicationGuid,
      routingNumber
    };

    return this.bankVerificationApi.bankVerificationManualLinkBankAccount(
      data,
      this.authorization
    )
    .map(response => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  postManualInitiation(
    bankName: string,
    routingNumber: string,
    accountNumber: string,
    accountType: LDCLPBPPublicApiModelsBanksInitiateTrialDepositsRequest.AccountTypeEnum,
    firstName: string,
    lastName: string,
    acceptedTermsAndConditions: boolean): Observable<LDCLPBPPublicApiModelsBanksInitiateTrialDepositsResponse> {

    let data: LDCLPBPPublicApiModelsBanksInitiateTrialDepositsRequest = {
      accountNumber,
      accountType,
      bankName,
      firstName,
      lastName,
      loanApplicationGuid: this.loanApplicationGuid,
      routingNumber,
      acceptedTermsAndConditions
    };

    return this.bankVerificationApi.bankVerificationInitiateTrialDeposits(
      data,
      this.authorization
    )
    .map(response => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  verifyTrialDeposits(
    bankVerifyGuid: string,
    amount1: number,
    amount2: number): Observable<TrialDepositsResponseData> {

    const request: TrialDepositsRequest = {
      amount1,
      amount2
    };

    return this.bankVerificationApi.bankVerificationVerifyTrialDeposits(
      bankVerifyGuid,
      request,
      this.authorization
    )
    .map((response: TrialDepositsResponse) => response.data)
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }
}
